import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, MapPin, DollarSign, ExternalLink, FileUp, Sparkles, Camera, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import CreateInspectionDialog from '../components/CreateInspectionDialog';
import ReverseEngineerDialog from '../components/ReverseEngineerDialog';
import FastOCRImportDialog from '../components/FastOCRImportDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InspectionsPage() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showReverseEngineerDialog, setShowReverseEngineerDialog] = useState(false);
  const [showFastOCRDialog, setShowFastOCRDialog] = useState(false);

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      const response = await axios.get(`${API}/inspections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to load inspections');
    } finally {
      setLoading(false);
    }
  };

  const canCreate = ['director', 'surveyor', 'admin'].includes(user?.role);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-accent text-white';
      case 'in_progress':
        return 'bg-warning text-white';
      case 'completed':
        return 'bg-success text-white';
      default:
        return 'bg-secondary';
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="inspections-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-primary mb-2" data-testid="inspections-title">Inspections</h1>
            <p className="text-muted-foreground">Manage property inspections and valuations</p>
          </div>
          {canCreate && (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowFastOCRDialog(true)} 
                data-testid="fast-ocr-button"
                className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 hover:from-purple-100 hover:to-blue-100"
              >
                <Zap className="h-4 w-4 mr-2 text-purple-600" />
                Fast Import
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowReverseEngineerDialog(true)} 
                data-testid="import-pdf-button"
              >
                <FileUp className="h-4 w-4 mr-2" />
                Import from PDF
              </Button>
              <Button onClick={() => setShowCreateDialog(true)} data-testid="create-inspection-button">
                <Plus className="h-4 w-4 mr-2" />
                New Inspection
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : inspections.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-muted-foreground mb-4">
                <MapPin className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No inspections found</p>
              </div>
              {canCreate && (
                <Button onClick={() => setShowCreateDialog(true)} data-testid="empty-state-create-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Inspection
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="inspections-list">
            {inspections.map((inspection, index) => {
              const hasValidId = inspection.id && inspection.id !== '';
              
              const handleCardClick = () => {
                if (hasValidId) {
                  navigate(`/inspections/${inspection.id}`);
                } else {
                  toast.error('Unable to open inspection - ID unavailable');
                }
              };

              return (
                <motion.div
                  key={inspection.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className={`transition-all duration-300 ${
                      hasValidId 
                        ? 'hover:shadow-lg hover:border-primary/50 cursor-pointer group' 
                        : 'opacity-75 cursor-not-allowed'
                    }`}
                    onClick={handleCardClick}
                    data-testid={`inspection-card-${inspection.id || index}`}
                  >
                    <CardHeader>
                      <div className="flex justify-between items-start mb-2">
                        <CardTitle 
                          className={`text-lg ${
                            hasValidId 
                              ? 'group-hover:text-primary group-hover:underline transition-colors' 
                              : ''
                          }`}
                          data-testid={`inspection-address-${inspection.id || index}`}
                        >
                          {inspection.property_address || 'Unknown Address'}
                        </CardTitle>
                        <Badge className={getStatusColor(inspection.status)} data-testid={`inspection-status-${inspection.id || index}`}>
                          {inspection.status?.replace('_', ' ') || 'Unknown'}
                        </Badge>
                      </div>
                      {!hasValidId && (
                        <span className="text-xs text-destructive">ID Unavailable</span>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {inspection.valuation_amount && (
                        <div className="flex items-center text-sm" data-testid={`inspection-valuation-${inspection.id || index}`}>
                          <DollarSign className="h-4 w-4 mr-2 text-success" />
                          <span className="font-mono font-medium">
                            ${inspection.valuation_amount.toLocaleString()}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-muted-foreground">
                          Created: {new Date(inspection.created_at).toLocaleDateString()}
                        </div>
                        {hasValidId && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/inspections/${inspection.id}`);
                            }}
                            data-testid={`open-inspection-btn-${inspection.id}`}
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Open
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {showCreateDialog && (
        <CreateInspectionDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onSuccess={() => {
            setShowCreateDialog(false);
            fetchInspections();
          }}
        />
      )}

      <ReverseEngineerDialog
        open={showReverseEngineerDialog}
        onOpenChange={setShowReverseEngineerDialog}
        onSuccess={() => {
          fetchInspections();
        }}
      />

      <FastOCRImportDialog
        open={showFastOCRDialog}
        onOpenChange={setShowFastOCRDialog}
        onSuccess={(inspectionId) => {
          setShowFastOCRDialog(false);
          fetchInspections();
          if (inspectionId) {
            navigate(`/inspections/${inspectionId}`);
          }
        }}
      />
    </Layout>
  );
}
